import { type ColumnDataType, ComparisonOperator } from '@blaze-shared/api-contracts-domain';
import { type ColumnType } from '@blaze-shared/schema-domain';

export const SUPPORTED_ARRAY_TYPES: readonly ColumnType[] = Object.freeze([
  'Array',
  'Formula',
  'Link',
  'Object',
]);

// Information on which column datatypes are valid query values for the selected column type.
// For example, Boolean can only accept only other boolean columns (eg. IsCompany Eq HasEmployees) but Image
// can accept either another Image column or a String column. (eg. Image Eq ThumnailUrl).
// Also @see intelli-query.service.ts for which operators should show for a given column type
export const SUPPORTED_COLUMN_TYPES: Record<ColumnType, ColumnType[]> = {
  AccessRole: ['Tag'],
  Array: ['Array', 'Formula', 'Object'],
  AutoNumber: ['Array', 'Object'],
  Boolean: ['Array', 'Boolean', 'Formula', 'Object'],
  CreatedBy: ['Array', 'Object'],
  CreatedTime: ['Array', 'Object'],
  Date: ['Array', 'Date', 'DateTime', 'Formula', 'Object'],
  DateTime: ['Array', 'Date', 'DateTime', 'Formula', 'Object', 'Time'],
  Duration: ['Array', 'String', 'Object'],
  Email: ['Array', 'String', 'Object'],
  File: ['Array', 'File', 'Formula', 'Image', 'Link', 'Object', 'String'],
  Formula: [
    'Array',
    'Boolean',
    'Date',
    'DateTime',
    'File',
    'Formula',
    'Image',
    'Link',
    'Number',
    'Object',
    'String',
    'Tag',
    'Time',
  ],
  GeoPoint: ['Array', 'GeoPoint', 'Object'],
  Image: ['Array', 'File', 'Formula', 'Image', 'Link', 'Object', 'String'],
  LastUpdatedTime: ['Array', 'Object'],
  Link: ['Array', 'Formula', 'Link', 'Object', 'String'],
  Number: ['Array', 'Formula', 'Number', 'Object'],
  Object: ['Array', 'Formula', 'Object'],
  PhoneNumber: ['Array', 'Object', 'String'],
  ProfilePicture: ['Array', 'File', 'Formula', 'Image', 'Link', 'Object', 'String'],
  Rollup: ['Formula', 'Number'],
  SecureText: ['Array', 'Object', 'String'],
  Signature: ['Array', 'Object', 'Signature'],
  String: [
    'Array',
    'Date',
    'DateTime',
    'File',
    'Formula',
    'Image',
    'Link',
    'Object',
    'String',
    'Tag',
  ],
  Tag: ['Array', 'Formula', 'Object', 'String', 'Tag'],
  Time: ['Array', 'Formula', 'Object', 'Time'],
  TimeZone: ['Array', 'Object', 'String', 'TimeZone'],
  UpdatedBy: ['Array', 'Object'],
  Url: ['Array', 'Object', 'String'],
  ZipCode: ['Array', 'Object', 'String'],
};

export const SUPPORTED_OPERATOR_COLUMN_TYPES_SINGLE: Record<ComparisonOperator, ColumnType[]> = {
  ContainsText: ['Array', 'File', 'Formula', 'Image', 'Object', 'String', 'Tag'],
  Eq: [
    'Array',
    'Boolean',
    'Date',
    'DateTime',
    'File',
    'Formula',
    'Image',
    'Link',
    'Number',
    'Object',
    'String',
    'Tag',
    'Time',
  ],
  EqIgnoreCase: ['Array', 'Formula', 'Link', 'Object', 'String', 'Tag'],
  Gt: ['Array', 'Date', 'DateTime', 'Formula', 'Link', 'Number', 'Object'],
  Gte: ['Array', 'Date', 'DateTime', 'Formula', 'Link', 'Number', 'Object'],
  HasPrefix: ['Array', 'File', 'Formula', 'Image', 'Link', 'Object', 'String', 'Tag'],
  HasPrefixIgnoreCase: ['Array', 'File', 'Formula', 'Image', 'Link', 'Object', 'String', 'Tag'],
  In: [...SUPPORTED_ARRAY_TYPES],
  IsFalse: ['Array', 'Boolean', 'Formula', 'Link', 'Object'],
  IsNotNull: [
    'Array',
    'Boolean',
    'Date',
    'DateTime',
    'File',
    'Formula',
    'Image',
    'Link',
    'Number',
    'Object',
    'String',
    'Tag',
    'Time',
  ],
  IsNull: [
    'Array',
    'Boolean',
    'Date',
    'DateTime',
    'File',
    'Formula',
    'Image',
    'Link',
    'Number',
    'Object',
    'String',
    'Tag',
    'Time',
  ],
  IsTrue: ['Array', 'Boolean', 'Formula', 'Object'],
  Lt: ['Array', 'Date', 'DateTime', 'Formula', 'Link', 'Number', 'Object'],
  Lte: ['Array', 'Date', 'DateTime', 'Formula', 'Link', 'Number', 'Object'],
  Near: ['Array', 'Object'],
  Neq: [
    'Array',
    'Boolean',
    'Date',
    'DateTime',
    'File',
    'Formula',
    'Image',
    'Link',
    'Number',
    'Object',
    'String',
    'Tag',
    'Time',
  ],
  NotIn: [...SUPPORTED_ARRAY_TYPES],
};

// Overrides of SUPPORTED_OPERATOR_COLUMN_TYPES_SINGLE for column that has multipleValues set to true
export const SUPPORTED_OPERATOR_COLUMN_TYPES_MULTIPLE: Record<ComparisonOperator, ColumnType[]> = {
  ...SUPPORTED_OPERATOR_COLUMN_TYPES_SINGLE,
  In: ['Array', 'Formula', 'Link', 'String', 'Object', 'Tag'], // In -> contains
  NotIn: ['Array', 'Formula', 'Link', 'Object', 'String', 'Tag'], // NotIn -> does not contain
};

// A list of available operator names. This is a list of what's presented to the user for the given operator.
// In addition, this includes names presented to the user that are sometimes different based on the selected
// column type (for example 'is', 'contains', etc -- @see intelli-query.service)
export enum OperatorName {
  Contains = 'contains',
  ContainsText = 'contains text',
  DoesNotContain = 'does not contain',
  Eq = 'equals to',
  EqIgnoreCase = 'equals to (ignore case)',
  Gt = 'is greater than',
  Gte = 'is greater than or equal to',
  HasPrefix = 'starts with',
  HasPrefixIgnoreCase = 'starts with (ignore case)',
  In = 'is in',
  Is = 'is',
  IsFalse = 'is false',
  IsNotNull = 'is not empty',
  IsNull = 'is empty',
  IsTrue = 'is true',
  Lt = 'is less than',
  Lte = 'is less than or equal to',
  Near = 'is near',
  Neq = 'is not equal to',
  NotIn = 'is not in',
}

// Default text displayed to the user for the given operator
export const DEFAULT_OPERATOR_NAMES: Record<ComparisonOperator, OperatorName> = {
  ContainsText: OperatorName.ContainsText,
  Eq: OperatorName.Eq,
  EqIgnoreCase: OperatorName.EqIgnoreCase,
  Gt: OperatorName.Gt,
  Gte: OperatorName.Gte,
  HasPrefix: OperatorName.HasPrefix,
  HasPrefixIgnoreCase: OperatorName.HasPrefixIgnoreCase,
  In: OperatorName.In,
  IsFalse: OperatorName.IsFalse,
  IsNotNull: OperatorName.IsNotNull,
  IsNull: OperatorName.IsNull,
  IsTrue: OperatorName.IsTrue,
  Lt: OperatorName.Lt,
  Lte: OperatorName.Lte,
  Near: OperatorName.Near,
  Neq: OperatorName.Neq,
  NotIn: OperatorName.NotIn,
};

// Maximum number of input parameters given operator name supports. We map it to operator name and not operator
// because backend can treat same operator differently based on the target column. If for example the target column
// for Eq operator is a link, the operator becomes 'is' and supports only one value.
export const MAX_VALUES_PER_OPERATOR_NAME: Record<OperatorName, number | undefined> = {
  'contains text': undefined,
  contains: undefined,
  'does not contain': undefined,
  'equals to (ignore case)': undefined,
  'equals to': undefined,
  'is empty': undefined,
  'is false': undefined,
  'is greater than or equal to': undefined,
  'is greater than': undefined,
  'is in': undefined,
  'is less than or equal to': undefined,
  'is less than': undefined,
  'is near': undefined,
  'is not empty': undefined,
  'is not equal to': undefined,
  'is not in': undefined,
  'is true': undefined,
  is: 1,
  'starts with (ignore case)': undefined,
  'starts with': undefined,
};

export const CONDITIONS_WITHOUT_VALUE: ComparisonOperator[] = [
  ComparisonOperator.IsFalse,
  ComparisonOperator.IsNotNull,
  ComparisonOperator.IsNull,
  ComparisonOperator.IsTrue,
];

export const OPERATORS_FOR_DATATYPES: Record<ColumnDataType, ComparisonOperator[] | undefined> = {
  Array: [
    ComparisonOperator.Eq,
    ComparisonOperator.NotIn,
    ComparisonOperator.IsNull,
    ComparisonOperator.IsNotNull,
  ],
  AutoNumber: [],
  Boolean: [
    ComparisonOperator.Eq,
    ComparisonOperator.IsFalse,
    ComparisonOperator.IsNotNull,
    ComparisonOperator.IsNull,
    ComparisonOperator.IsTrue,
    ComparisonOperator.Neq,
  ],
  CreatedBy: [],
  CreatedTime: [],
  Date: [
    ComparisonOperator.Eq,
    ComparisonOperator.Gt,
    ComparisonOperator.Gte,
    ComparisonOperator.In,
    ComparisonOperator.IsNotNull,
    ComparisonOperator.IsNull,
    ComparisonOperator.Lt,
    ComparisonOperator.Lte,
    ComparisonOperator.Neq,
    ComparisonOperator.NotIn,
  ],
  DateTime: [
    ComparisonOperator.Eq,
    ComparisonOperator.Gt,
    ComparisonOperator.Gte,
    ComparisonOperator.In,
    ComparisonOperator.IsNotNull,
    ComparisonOperator.IsNull,
    ComparisonOperator.Lt,
    ComparisonOperator.Lte,
    ComparisonOperator.Neq,
    ComparisonOperator.NotIn,
  ],
  Duration: [
    ComparisonOperator.Eq,
    ComparisonOperator.Gt,
    ComparisonOperator.Gte,
    ComparisonOperator.In,
    ComparisonOperator.IsNotNull,
    ComparisonOperator.IsNull,
    ComparisonOperator.Lt,
    ComparisonOperator.Lte,
    ComparisonOperator.Neq,
    ComparisonOperator.NotIn,
  ],
  File: [
    ComparisonOperator.ContainsText,
    ComparisonOperator.In,
    ComparisonOperator.IsNotNull,
    ComparisonOperator.IsNull,
    ComparisonOperator.NotIn,
  ],
  Formula: [],
  GeoPoint: undefined,
  Image: [
    ComparisonOperator.ContainsText,
    ComparisonOperator.In,
    ComparisonOperator.IsNotNull,
    ComparisonOperator.IsNull,
    ComparisonOperator.NotIn,
  ],
  LastUpdatedTime: [],
  Link: [
    ComparisonOperator.ContainsText,
    ComparisonOperator.Eq,
    ComparisonOperator.HasPrefix,
    ComparisonOperator.HasPrefixIgnoreCase,
    ComparisonOperator.In,
    ComparisonOperator.IsNotNull,
    ComparisonOperator.IsNull,
    ComparisonOperator.Neq,
    ComparisonOperator.NotIn,
  ],
  Number: [
    ComparisonOperator.Eq,
    ComparisonOperator.Gt,
    ComparisonOperator.Gte,
    ComparisonOperator.In,
    ComparisonOperator.IsNotNull,
    ComparisonOperator.IsNull,
    ComparisonOperator.Lt,
    ComparisonOperator.Lte,
    ComparisonOperator.Neq,
    ComparisonOperator.NotIn,
  ],
  Object: undefined,
  Rollup: [
    ComparisonOperator.Eq,
    ComparisonOperator.Gt,
    ComparisonOperator.Gte,
    ComparisonOperator.In,
    ComparisonOperator.IsNotNull,
    ComparisonOperator.IsNull,
    ComparisonOperator.Lt,
    ComparisonOperator.Lte,
    ComparisonOperator.Neq,
    ComparisonOperator.NotIn,
  ],
  Signature: undefined,
  String: [
    ComparisonOperator.ContainsText,
    ComparisonOperator.Eq,
    ComparisonOperator.EqIgnoreCase,
    ComparisonOperator.HasPrefix,
    ComparisonOperator.HasPrefixIgnoreCase,
    ComparisonOperator.In,
    ComparisonOperator.IsNotNull,
    ComparisonOperator.IsNull,
    ComparisonOperator.Neq,
    ComparisonOperator.NotIn,
  ],
  Tag: [
    ComparisonOperator.ContainsText,
    ComparisonOperator.Eq,
    ComparisonOperator.HasPrefix,
    ComparisonOperator.HasPrefixIgnoreCase,
    ComparisonOperator.In,
    ComparisonOperator.IsNotNull,
    ComparisonOperator.IsNull,
    ComparisonOperator.Neq,
    ComparisonOperator.NotIn,
  ],
  Time: [
    ComparisonOperator.Eq,
    ComparisonOperator.Gt,
    ComparisonOperator.Gte,
    ComparisonOperator.In,
    ComparisonOperator.IsNotNull,
    ComparisonOperator.IsNull,
    ComparisonOperator.Lt,
    ComparisonOperator.Lte,
    ComparisonOperator.Neq,
    ComparisonOperator.NotIn,
  ],
  UpdatedBy: [],
};
